import React from 'react';
import {
  Smartphone,
  Tablet,
  Monitor,
  Eye,
  EyeOff,
  RotateCcw,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Info,
  Grid,
  Type,
  MoveVertical,
  Maximize,
  ArrowDownUp,
} from 'lucide-react';
import { PageSection, ComponentStyle, DeviceBreakpoint } from '../../../types';
import { useSite } from '../../../context/SiteContext';
import {
  isFieldOverridden,
  getInheritanceSourceLabel,
  applyStyleChange,
  resetFieldOverride,
  resetAllDeviceOverrides,
  resolveStyleForDevice,
} from '../../../utils/responsive';
import { autoFixSectionResponsive } from '../../../utils/responsiveQA';

interface ResponsiveInspectorProps {
  section: PageSection;
  onUpdateStyle: (updates: Partial<ComponentStyle>) => void;
}

export const ResponsiveInspector: React.FC<ResponsiveInspectorProps> = ({
  section,
  onUpdateStyle,
}) => {
  const { deviceMode, setDeviceMode, updateSectionInActivePage } = useSite();
  const style = section.style || {};

  const resolved = resolveStyleForDevice(style, deviceMode);

  const handleDeviceChange = (mode: DeviceBreakpoint) => {
    setDeviceMode(mode);
  };

  const handlePropertyUpdate = (fieldUpdates: Partial<ComponentStyle>) => {
    const nextStyle = applyStyleChange(style, deviceMode, fieldUpdates);
    onUpdateStyle(nextStyle);
  };

  const handleResetField = (field: any) => {
    const nextStyle = resetFieldOverride(style, deviceMode, field);
    onUpdateStyle(nextStyle);
  };

  const handleResetAllForDevice = () => {
    const nextStyle = resetAllDeviceOverrides(style, deviceMode);
    onUpdateStyle(nextStyle);
  };

  const handleAutoFix = () => {
    const fixedSection = autoFixSectionResponsive(section);
    updateSectionInActivePage(section.id, fixedSection.content, fixedSection.style);
  };

  const isMobile = deviceMode === 'mobile';
  const isTablet = deviceMode === 'tablet';
  const isDesktop = deviceMode === 'desktop';

  const hasAnyMobileOverrides = !!style.mobileOverrides && Object.keys(style.mobileOverrides).length > 0;
  const hasAnyTabletOverrides = !!style.tabletOverrides && Object.keys(style.tabletOverrides).length > 0;

  return (
    <div className="space-y-5">
      {/* Device Mode Switcher Bar */}
      <div className="p-3 bg-slate-950/70 rounded-2xl border border-slate-800 space-y-2.5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold text-slate-300">Aktif Düzenleme Cihazı:</span>
          <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
            {isMobile ? '📱 Mobil Cihaz' : isTablet ? '📱 Tablet Cihaz' : '🖥️ Masaüstü (Miras)'}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-900 rounded-xl border border-slate-800">
          <button
            type="button"
            onClick={() => handleDeviceChange('desktop')}
            className={`py-2 px-1 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition ${
              isDesktop
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Monitor className="w-3.5 h-3.5" />
            <span>Masaüstü</span>
          </button>
          <button
            type="button"
            onClick={() => handleDeviceChange('tablet')}
            className={`py-2 px-1 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition ${
              isTablet
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Tablet className="w-3.5 h-3.5" />
            <span>Tablet</span>
          </button>
          <button
            type="button"
            onClick={() => handleDeviceChange('mobile')}
            className={`py-2 px-1 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition ${
              isMobile
                ? 'bg-blue-600 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            <Smartphone className="w-3.5 h-3.5" />
            <span>Mobil</span>
          </button>
        </div>
      </div>

      {/* Inheritance Context Notice */}
      {isMobile ? (
        <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-300 text-xs flex items-start gap-2.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-teal-400" />
          <div className="space-y-1">
            <p className="font-bold text-teal-200">Mobil Öncelikli Düzenleme</p>
            <p className="text-[11px] text-teal-300/80 leading-relaxed">
              Bu modda yapacağınız değişiklikler <strong>yalnızca mobil ekranları</strong> etkiler. Masaüstü tasarımınız bozulmaz.
            </p>
          </div>
        </div>
      ) : isTablet ? (
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-start gap-2.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
          <div className="space-y-1">
            <p className="font-bold text-amber-200">Tablet Düzenleme Modu</p>
            <p className="text-[11px] text-amber-300/80 leading-relaxed">
              Tablet ekranları için (768px - 1024px) özel kolon ve dikey dolgu ayarlayabilirsiniz.
            </p>
          </div>
        </div>
      ) : (
        <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs flex items-start gap-2.5">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-400" />
          <div className="space-y-1">
            <p className="font-bold text-blue-200">Masaüstü Ana Tasarım (Cascade Kaynağı)</p>
            <p className="text-[11px] text-blue-300/80 leading-relaxed">
              Buradaki ayarlar tüm cihazlara miras bırakılır. Özel mobil değer verilmedikçe mobil de bu değerleri kullanır.
            </p>
          </div>
        </div>
      )}

      {/* 1. Device Column Override */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Grid className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">
              {isMobile ? 'Mobil Kolon Sayısı' : isTablet ? 'Tablet Kolon Sayısı' : 'Masaüstü Kolon Sayısı'}
            </h4>
          </div>

          {!isDesktop && isFieldOverridden(style, deviceMode, 'columns') && (
            <button
              type="button"
              onClick={() => handleResetField('columns')}
              className="text-[10px] font-bold text-amber-400 hover:text-amber-300 flex items-center gap-1 transition"
              title="Miras alınan değere geri dön"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Sıfırla</span>
            </button>
          )}
        </div>

        <div className="flex items-center justify-between text-[11px] text-slate-400">
          <span>Durum:</span>
          <span className="font-semibold text-slate-200">
            {getInheritanceSourceLabel(style, deviceMode, 'columns')}
          </span>
        </div>

        <div className="grid grid-cols-4 gap-1.5">
          {[1, 2, 3, 4].map((colNum) => (
            <button
              key={colNum}
              type="button"
              onClick={() => handlePropertyUpdate({ columns: colNum })}
              className={`py-2 px-1 rounded-xl text-xs font-bold transition text-center ${
                resolved.columns === colNum
                  ? 'bg-blue-600 text-white shadow-md ring-1 ring-blue-400'
                  : 'bg-slate-900 text-slate-300 border border-slate-700 hover:bg-slate-800'
              }`}
            >
              {colNum} Kolon
            </button>
          ))}
        </div>
      </div>

      {/* 2. Device Vertical Height / Padding */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MoveVertical className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">
              {isMobile ? 'Mobil Dikey Dolgu (Padding)' : 'Dikey Yükseklik'}
            </h4>
          </div>

          {!isDesktop && isFieldOverridden(style, deviceMode, 'sectionHeight') && (
            <button
              type="button"
              onClick={() => handleResetField('sectionHeight')}
              className="text-[10px] font-bold text-amber-400 hover:text-amber-300 flex items-center gap-1 transition"
            >
              <RotateCcw className="w-3 h-3" />
              <span>Sıfırla</span>
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'compact', label: 'Kompakt', desc: 'py-10 (az boşluk)' },
            { id: 'normal', label: 'Standart', desc: 'py-16 (ideal)' },
            { id: 'spacious', label: 'Ferah', desc: 'py-24 (geniş)' },
            { id: 'fullscreen', label: 'Tam Ekran', desc: 'min-h-[85vh]' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handlePropertyUpdate({ sectionHeight: opt.id as any })}
              className={`p-2.5 rounded-xl border text-left transition ${
                resolved.sectionHeight === opt.id
                  ? 'bg-blue-600/20 border-blue-500 text-white shadow-sm ring-1 ring-blue-500/30'
                  : 'bg-slate-900/80 border-slate-700 text-slate-300 hover:border-slate-600'
              }`}
            >
              <div className="text-xs font-bold">{opt.label}</div>
              <div className="text-[10px] text-slate-400">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 3. Button Layout on Mobile */}
      {isMobile && (
        <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Maximize className="w-3.5 h-3.5 text-blue-400" />
              <h4 className="text-xs font-bold text-slate-200">Mobil Buton Genişliği</h4>
            </div>

            {isFieldOverridden(style, 'mobile', 'buttonWidth') && (
              <button
                type="button"
                onClick={() => handleResetField('buttonWidth')}
                className="text-[10px] font-bold text-amber-400 hover:text-amber-300 flex items-center gap-1 transition"
              >
                <RotateCcw className="w-3 h-3" />
                <span>Sıfırla</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handlePropertyUpdate({ buttonWidth: 'full' })}
              className={`p-2.5 rounded-xl border text-center text-xs font-bold transition ${
                resolved.buttonWidth === 'full'
                  ? 'bg-blue-600 text-white border-blue-500 shadow-md'
                  : 'bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800'
              }`}
            >
              Tam Genişlik (W-Full)
            </button>
            <button
              type="button"
              onClick={() => handlePropertyUpdate({ buttonWidth: 'auto' })}
              className={`p-2.5 rounded-xl border text-center text-xs font-bold transition ${
                resolved.buttonWidth === 'auto'
                  ? 'bg-blue-600 text-white border-blue-500 shadow-md'
                  : 'bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800'
              }`}
            >
              Otomatik Boyut (Auto)
            </button>
          </div>
        </div>
      )}

      {/* 4. Device Visibility Control */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center gap-2">
          <Eye className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Cihaz Bazlı Görünürlük</h4>
        </div>

        <div className="space-y-2">
          {/* Desktop Visibility */}
          <label className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-slate-700 cursor-pointer hover:bg-slate-800/60 transition">
            <div className="flex items-center gap-2.5">
              <Monitor className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Masaüstünde Göster</span>
            </div>
            <input
              type="checkbox"
              checked={!style.hideOnDesktop && !style.responsive?.hideOnDesktop}
              onChange={(e) =>
                onUpdateStyle({
                  hideOnDesktop: !e.target.checked,
                  responsive: {
                    ...(style.responsive || {}),
                    hideOnDesktop: !e.target.checked,
                  },
                })
              }
              className="w-4 h-4 text-blue-600 rounded bg-slate-800 border-slate-600 focus:ring-blue-500"
            />
          </label>

          {/* Tablet Visibility */}
          <label className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-slate-700 cursor-pointer hover:bg-slate-800/60 transition">
            <div className="flex items-center gap-2.5">
              <Tablet className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Tablette Göster</span>
            </div>
            <input
              type="checkbox"
              checked={!style.hideOnTablet && !style.responsive?.hideOnTablet}
              onChange={(e) =>
                onUpdateStyle({
                  hideOnTablet: !e.target.checked,
                  responsive: {
                    ...(style.responsive || {}),
                    hideOnTablet: !e.target.checked,
                  },
                })
              }
              className="w-4 h-4 text-blue-600 rounded bg-slate-800 border-slate-600 focus:ring-blue-500"
            />
          </label>

          {/* Mobile Visibility */}
          <label className="flex items-center justify-between p-2.5 rounded-xl bg-slate-900/80 border border-slate-700 cursor-pointer hover:bg-slate-800/60 transition">
            <div className="flex items-center gap-2.5">
              <Smartphone className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-semibold text-slate-200">Mobilde Göster</span>
            </div>
            <input
              type="checkbox"
              checked={!style.hideOnMobile && !style.responsive?.hideOnMobile}
              onChange={(e) =>
                onUpdateStyle({
                  hideOnMobile: !e.target.checked,
                  responsive: {
                    ...(style.responsive || {}),
                    hideOnMobile: !e.target.checked,
                  },
                })
              }
              className="w-4 h-4 text-blue-600 rounded bg-slate-800 border-slate-600 focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      {/* 5. 1-Click Responsive Auto-Optimize */}
      <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border border-blue-500/30 space-y-2.5">
        <div className="flex items-center gap-2 text-blue-300">
          <Sparkles className="w-4 h-4" />
          <h4 className="text-xs font-bold">1-Tıkla Mobil Optimizasyonu</h4>
        </div>
        <p className="text-[11px] text-slate-300 leading-relaxed">
          Bu bölüm için en ideal mobil kolon (1 kolon), dikey dolgu ve buton genişliği ayarlarını otomatik uygular.
        </p>
        <button
          type="button"
          onClick={handleAutoFix}
          className="w-full py-2 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-lg transition flex items-center justify-center gap-1.5"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>Mobille Tam Uyumlu Yap</span>
        </button>
      </div>

      {/* Reset All Overrides Button */}
      {(hasAnyMobileOverrides || hasAnyTabletOverrides) && (
        <button
          type="button"
          onClick={handleResetAllForDevice}
          className="w-full py-2.5 px-3 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/20 text-xs font-bold transition flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          <span>Bu Cihazdaki Tüm Özel Değerleri Sıfırla</span>
        </button>
      )}
    </div>
  );
};
